import {
  EmployeeListOrder,
  EmployeeListOrderBy,
  EmployeePositionStatusValue,
  EmployeesListParams,
} from "~/entity/Employee/model/schema";
import {
  EmployeePositionStatus,
  OrderByColumnInEmployees,
  SortType,
} from "~/schemas/generated/graphql";

import { IBaseStaffFilterForm } from "./baseStaffUrlParams";

const TRAILING_MASK_CHARS = ["_", "-"];

const trimTrailingMaskChars = (value: string): string => {
  const reversed = value.split("").reverse();
  while (reversed.length && TRAILING_MASK_CHARS.includes(reversed[0])) {
    reversed.shift();
  }
  return reversed.reverse().join("");
};

export const parseWorkPhoneForQuery = (
  workPhone?: string,
): string | undefined => {
  if (!workPhone) {
    return undefined;
  }
  const trimmed = trimTrailingMaskChars(workPhone);
  return trimmed || undefined;
};

const DEFAULT_STATUSES: EmployeePositionStatusValue[] = [
  "temporaryUnavailable",
  "verified",
  "unverified",
  "sendCheck",
  "rejected",
];

const toEndOfDay = (date?: Date | null): Date | undefined => {
  if (!date) {
    return undefined;
  }
  const result = new Date(date);
  result.setHours(23, 59, 59, 0);
  return result;
};

const buildDateRange = (
  from?: Date | null,
  to?: Date | null,
): { from: Date | undefined; to: Date | undefined } => ({
  from: from ? new Date(from) : undefined,
  to: toEndOfDay(to),
});

const resolveIds = (options: { value: string; label: string }[]): string[] =>
  options.length > 0 ? options.map((item) => item.value) : [];

const toNullableIds = (ids: string[]): Array<string | null> =>
  ids.length > 0 ? ids : [];

const toRestStatus = (status: string): EmployeePositionStatusValue =>
  (status.charAt(0).toLowerCase() +
    status.slice(1)) as EmployeePositionStatusValue;

const resolveStatuses = (status?: string): EmployeePositionStatusValue[] =>
  status ? [toRestStatus(status)] : DEFAULT_STATUSES;

const toIso = (date?: Date | null) => (date ? date.toISOString() : undefined);

const ORDER_BY_TO_GRAPHQL: Record<
  EmployeeListOrderBy,
  OrderByColumnInEmployees
> = {
  id: OrderByColumnInEmployees.Id,
  name: OrderByColumnInEmployees.Name,
  status: OrderByColumnInEmployees.Status,
  createdAt: OrderByColumnInEmployees.CreatedAt,
};

interface IMapBaseStaffFilterToQueryArgs {
  filter: IBaseStaffFilterForm;
  searchText?: string;
  count: number;
  page: number;
  resolvedOrderBy: EmployeeListOrderBy;
  resolvedOrder: EmployeeListOrder;
}

export const mapBaseStaffFilterToQuery = ({
  filter,
  searchText,
  count,
  page,
  resolvedOrderBy,
  resolvedOrder,
}: IMapBaseStaffFilterToQueryArgs): EmployeesListParams => {
  const creationRange = buildDateRange(filter.creationFrom, filter.creationTo);
  const lastEditRange = buildDateRange(filter.lastEditFrom, filter.lastEditTo);
  const positionIds = resolveIds(filter.positionIds);
  const filterValues = resolveIds(filter.filterValues);
  const hasFilterValues = filterValues.length > 0;
  const filterBy = filter.filterBy;

  return {
    count,
    page,
    id: filter.id ? Number(filter.id) : undefined,
    creationFrom: toIso(creationRange.from),
    creationTo: toIso(creationRange.to),
    surname: filter.surname || undefined,
    searchText: searchText || undefined,
    lastEditFrom: toIso(lastEditRange.from),
    lastEditTo: toIso(lastEditRange.to),
    name: filter.name || undefined,
    middleName: filter.middleName || undefined,
    workPhone: parseWorkPhoneForQuery(filter.workPhone),
    email: filter.email || undefined,
    orderBy: resolvedOrderBy,
    order: resolvedOrder,
    dealerIds:
      filterBy === "dealer" && hasFilterValues
        ? filterValues.map(Number)
        : undefined,
    positionIds: positionIds.length ? positionIds.map(Number) : undefined,
    bindingTypes: filterBy === "partner" ? ["PARTNER"] : undefined,
    partnerIds:
      filterBy === "partner" && hasFilterValues ? filterValues : undefined,
    city: filterBy === "city" && hasFilterValues ? filterValues : undefined,
    region: filterBy === "region" && hasFilterValues ? filterValues : undefined,
    country:
      filterBy === "country" && hasFilterValues ? filterValues : undefined,
    federalDistrict:
      filterBy === "federalDistrict" && hasFilterValues
        ? filterValues
        : undefined,
    ladaAcademiaIds: filter.ladaAcademiaId
      ? [filter.ladaAcademiaId]
      : undefined,
    statuses: resolveStatuses(filter.status),
    emailVerification: filter.emailVerification?.value ?? undefined,
  };
};

export const mapBaseStaffFilterToReportQuery = ({
  filter,
  searchText,
}: Pick<IMapBaseStaffFilterToQueryArgs, "filter" | "searchText">): Record<
  string,
  unknown
> => {
  const creationRange = buildDateRange(filter.creationFrom, filter.creationTo);
  const lastEditRange = buildDateRange(filter.lastEditFrom, filter.lastEditTo);
  const dealerIds =
    filter.filterBy === "dealer" ? resolveIds(filter.filterValues) : [];
  const positionIds = resolveIds(filter.positionIds);

  return {
    DealerIds: toNullableIds(dealerIds),
    PositionIds: toNullableIds(positionIds),
    ladaAcademiaIds: filter.ladaAcademiaId
      ? [filter.ladaAcademiaId]
      : undefined,
    creationFrom: toIso(creationRange.from),
    creationTo: toIso(creationRange.to),
    lastEditFrom: toIso(lastEditRange.from),
    lastEditTo: toIso(lastEditRange.to),
    id: filter.id || undefined,
    surname: filter.surname || undefined,
    name: filter.name || undefined,
    middleName: filter.middleName || undefined,
    workPhone: parseWorkPhoneForQuery(filter.workPhone),
    email: filter.email || undefined,
    statuses: resolveStatuses(filter.status),
    emailVerification: filter.emailVerification?.value ?? undefined,
    searchText: searchText || undefined,
  };
};

export const mapBaseStaffFilterToSupplementQuery = ({
  filter,
  searchText,
  count,
  page,
  resolvedOrderBy,
  resolvedOrder,
}: IMapBaseStaffFilterToQueryArgs) => {
  const creationRange = buildDateRange(filter.creationFrom, filter.creationTo);
  const lastEditRange = buildDateRange(filter.lastEditFrom, filter.lastEditTo);
  const dealerIds =
    filter.filterBy === "dealer" ? resolveIds(filter.filterValues) : [];
  const positionIds = resolveIds(filter.positionIds);

  return {
    count,
    page,
    id: filter.id || undefined,
    creationFrom: creationRange.from,
    creationTo: creationRange.to,
    surname: filter.surname || undefined,
    searchText: searchText || undefined,
    lastEditFrom: lastEditRange.from,
    lastEditTo: lastEditRange.to,
    name: filter.name,
    middleName: filter.middleName,
    workPhone: parseWorkPhoneForQuery(filter.workPhone),
    email: filter.email || undefined,
    orderBy: ORDER_BY_TO_GRAPHQL[resolvedOrderBy],
    order: resolvedOrder === "DESC" ? SortType.Desc : SortType.Asc,
    DealerIds: toNullableIds(dealerIds) as Array<string | null>,
    PositionIds: toNullableIds(positionIds) as Array<string | null>,
    ladaAcademiaIds: filter.ladaAcademiaId
      ? ([filter.ladaAcademiaId] as Array<string | null>)
      : undefined,
    statuses: resolveStatuses(
      filter.status,
    ) as Array<EmployeePositionStatus | null>,
    emailVerification: filter.emailVerification?.value ?? null,
  };
};
